// valuePass/admin/src/lib/firebase.ts
//
// Firebase initialisation for the admin dashboard.
//
// ── Config resolution strategy ────────────────────────────────────────────────
//
//   Production (Firebase Hosting)
//     Firebase Hosting automatically serves the project's config at
//     /__/firebase/init.json. We fetch this at startup so no secrets are
//     embedded in the built bundle.
//
//   Development (local Vite server)
//     The /__/firebase/init.json endpoint is not available locally, so we
//     fall back to VITE_FIREBASE_* environment variables defined in .env.local.
//     Copy .env.example → .env.local and fill in the values from the Firebase
//     Console (Project Settings → Your apps → SDK setup).
//
// ── Auth & Firestore ──────────────────────────────────────────────────────────
//
//   The admin dashboard uses:
//     • Firebase Auth    — Google Sign-In for administrator access
//     • Firestore        — real-time listeners for analytics + beneficiary data
//
//   All write operations go through the Cloud Functions REST API (/api/*)
//   so that business-logic validation is always enforced server-side.
//   Firestore is used read-only from the client except where noted.
//
// ── Emulator support ──────────────────────────────────────────────────────────
//
//   Set VITE_USE_EMULATORS=true in .env.local to point Auth and Firestore
//   at the local Firebase Emulator Suite instead of production services.
//
// ─────────────────────────────────────────────────────────────────────────────

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getFirestore,
  connectFirestoreEmulator,
  type Firestore,
} from "firebase/firestore";
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  type Auth,
} from "firebase/auth";

// ── Config type ───────────────────────────────────────────────────────────────

interface FirebaseConfig {
  apiKey:            string;
  authDomain:        string;
  projectId:         string;
  storageBucket?:    string;
  messagingSenderId?: string;
  appId?:            string;
}

// ── Config resolution ─────────────────────────────────────────────────────────

/**
 * Fetches the Firebase config from the Firebase Hosting auto-endpoint.
 * Returns null when the endpoint is unavailable (local dev, non-Hosting serve).
 */
async function fetchHostingConfig(): Promise<FirebaseConfig | null> {
  try {
    const res = await fetch("/__/firebase/init.json");
    if (!res.ok) return null;
    return (await res.json()) as FirebaseConfig;
  } catch {
    return null;
  }
}

/**
 * Builds a Firebase config from VITE_FIREBASE_* environment variables.
 * Throws when required variables are missing so the developer is notified
 * immediately rather than getting a cryptic Firebase error later.
 */
function configFromEnv(): FirebaseConfig {
  const required: Array<keyof FirebaseConfig> = ["apiKey", "authDomain", "projectId"];

  const config: FirebaseConfig = {
    apiKey:            import.meta.env.VITE_FIREBASE_API_KEY            ?? "",
    authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN        ?? "",
    projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID         ?? "",
    storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET     ?? undefined,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? undefined,
    appId:             import.meta.env.VITE_FIREBASE_APP_ID              ?? undefined,
  };

  const missing = required.filter((k) => !config[k]);
  if (missing.length > 0) {
    throw new Error(
      `[firebase] Missing required environment variables: ${missing
        .map((k) => `VITE_FIREBASE_${k.replace(/([A-Z])/g, "_$1").toUpperCase()}`)
        .join(", ")}.\n` +
        "Copy .env.example → .env.local and fill in the values from the " +
        "Firebase Console (Project Settings → Your apps → SDK setup).",
    );
  }

  return config;
}

// ── Singleton initialisation ──────────────────────────────────────────────────
//
// Firebase must be initialised exactly once per application lifetime.
// We use module-level singletons rather than React context so that the same
// instances are available outside the component tree (e.g. in query functions).

let _app:       FirebaseApp | null = null;
let _db:        Firestore   | null = null;
let _auth:      Auth        | null = null;

let _initPromise: Promise<void> | null = null;

/**
 * Initialises the Firebase app, Firestore, and Auth.
 * Safe to call multiple times — subsequent calls return the same promise.
 *
 * @example
 * // In main.tsx, before rendering:
 * await initFirebase();
 */
export async function initFirebase(): Promise<void> {
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    // Try hosted config first; fall back to env vars for local dev.
    const config = (await fetchHostingConfig()) ?? configFromEnv();

    _app  = initializeApp(config);
    _db   = getFirestore(_app);
    _auth = getAuth(_app);

    // Point at local emulators when requested
    if (import.meta.env.VITE_USE_EMULATORS === "true") {
      const firestoreHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST ?? "127.0.0.1";
      const firestorePort = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? "8080");
      const authHost      = import.meta.env.VITE_AUTH_EMULATOR_HOST ?? "http://127.0.0.1:9099";

      connectFirestoreEmulator(_db, firestoreHost, firestorePort);
      connectAuthEmulator(_auth, authHost, { disableWarnings: true });

      console.info(
        `[firebase] Emulators active — Firestore: ${firestoreHost}:${firestorePort}, Auth: ${authHost}`,
      );
    }
  })();

  return _initPromise;
}

// ── Accessor functions ────────────────────────────────────────────────────────
//
// These throw if called before initFirebase() has resolved so that mistakes
// surface as clear errors during development rather than null-reference panics.

export function getDb(): Firestore {
  if (!_db) {
    throw new Error(
      "[firebase] Firestore is not initialised. " +
        "Ensure initFirebase() has resolved before calling getDb().",
    );
  }
  return _db;
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    throw new Error(
      "[firebase] Auth is not initialised. " +
        "Ensure initFirebase() has resolved before calling getFirebaseAuth().",
    );
  }
  return _auth;
}

// ── Auth providers ────────────────────────────────────────────────────────────

/**
 * Pre-configured Google Auth provider.
 * Requests the user's email and profile scopes by default.
 * The `hd` (hosted domain) parameter can be set to restrict sign-in to a
 * specific Google Workspace domain (e.g., "yourdomain.org").
 */
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");

// Set hosted domain restriction when provided
if (import.meta.env.VITE_GOOGLE_AUTH_HOSTED_DOMAIN) {
  googleProvider.setCustomParameters({
    hd: import.meta.env.VITE_GOOGLE_AUTH_HOSTED_DOMAIN as string,
  });
}
